import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { PasswordField } from '../../components/ui/PasswordField'
import { Modal } from '../../components/ui/Modal'
import { useConfirmare } from '../../components/ui/Confirmare'

/*
  Trimiterea si citirea sunt separate (23.10.2026). Pana atunci, o bifa „aceleasi date ca la SMTP"
  copia la salvare adresa de trimitere peste cea citita. Pe 22.10 cineva a schimbat adresa de
  trimitere pe contact@, citirea a trecut odata cu ea, iar CRM-ul a importat 20.097 de mailuri
  vechi si n-a mai citit contabilitate@. Acum CRM-ul trimite de pe no-reply@ si citeste
  contabilitate@: doua conturi, fiecare cu datele lui.
*/
const Schema = z.object({
  smtpHost: z.string().min(1, 'Host obligatoriu'),
  smtpPort: z.coerce.number().int().min(1).max(65535),
  smtpUser: z.string().min(1, 'Utilizator obligatoriu'),
  smtpPassword: z.string().optional(),
  imapHost: z.string().min(1, 'Host obligatoriu'),
  imapPort: z.coerce.number().int().min(1).max(65535),
  imapUser: z.string().min(1, 'Utilizator obligatoriu'),
  imapPassword: z.string().optional(),
})

type Input = z.infer<typeof Schema>

interface ServerSettings {
  smtpHost: string
  smtpPort: number
  smtpUser: string
  smtpPassSet: boolean
  imapHost: string
  imapPort: number
  imapUser: string
  imapPassSet: boolean
  imapLastSeenAt: string | null
}

interface SettingsResponse {
  settings: ServerSettings | null
}

interface TestResult {
  ok: boolean
  service: 'smtp' | 'imap'
  error?: string
}

export function SetariEmailPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.email:write')
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const { confirma } = useConfirmare()

  const query = useQuery<SettingsResponse>({
    queryKey: ['settings', 'email'],
    queryFn: async () => (await api.get<SettingsResponse>('/settings/email')).data,
  })

  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPassword: '',
      imapHost: '',
      imapPort: 993,
      imapUser: '',
      imapPassword: '',
    },
  })

  useEffect(() => {
    if (query.data?.settings) {
      const s = query.data.settings
      form.reset({
        smtpHost: s.smtpHost,
        smtpPort: s.smtpPort,
        smtpUser: s.smtpUser,
        smtpPassword: '',
        imapHost: s.imapHost,
        imapPort: s.imapPort,
        imapUser: s.imapUser,
        imapPassword: '',
      })
    }
  }, [query.data, form])

  const saveMutation = useMutation({
    mutationFn: async (data: Input) =>
      (await api.put<SettingsResponse>('/settings/email', data)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'email'], data)
      form.setValue('smtpPassword', '')
      form.setValue('imapPassword', '')
      toast.success('Setările de email au fost salvate')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la salvare'
      toast.error(message)
    },
  })

  const testMutation = useMutation({
    mutationFn: async (service: 'smtp' | 'imap') => {
      // Send the current form values so user can test BEFORE saving.
      // Empty password falls back to whatever's already in DB (per-field merge BE-side).
      const v = form.getValues()
      const smtp = service === 'smtp'
      const payload = {
        service,
        host: (smtp ? v.smtpHost : v.imapHost) || undefined,
        port: smtp ? v.smtpPort : v.imapPort,
        user: (smtp ? v.smtpUser : v.imapUser) || undefined,
        password: (smtp ? v.smtpPassword : v.imapPassword) || undefined,
      }
      return (await api.post<TestResult>('/settings/email/test', payload)).data
    },
    onMutate: () => setTestResult(null),
    onSuccess: (data) => setTestResult(data),
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la test'
      setTestResult({ ok: false, service: 'smtp', error: message })
    },
  })

  const settings = query.data?.settings

  // Schimbarea casutei citite muta tot ce preia CRM-ul (extrase, facturi, raspunsuri): se cere
  // confirmare, cu numele ambelor adrese.
  const salveaza = async (data: Input) => {
    const vechi = settings?.imapUser?.trim().toLowerCase()
    const nou = data.imapUser.trim().toLowerCase()
    if (vechi && vechi !== nou) {
      const da = await confirma({
        titlu: 'Schimbi căsuța citită de CRM?',
        mesaj: (
          <>
            CRM-ul nu va mai citi <strong>{settings?.imapUser}</strong> și va prelua mailurile din{' '}
            <strong>{data.imapUser}</strong>: extrase, facturi, răspunsuri la cereri și task-urile
            din mail. Din căsuța nouă se preiau doar mailurile din ultimele 3 zile.
          </>
        ),
        buton: 'Schimbă căsuța citită',
        pericol: true,
      })
      if (!da) return
    }
    saveMutation.mutate(data)
  }

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-xl font-semibold">SMTP / IMAP</h1>
      <Card description="Două conturi separate: cel de pe care CRM-ul trimite mailuri (notificări, rapoarte, cereri către tipografii, influenceri etc.) și căsuța principală pe care CRM-ul o citește. Parolele sunt păstrate criptat.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : (
          // Hidden dummy fields to absorb Chrome's auto-fill (it targets the
          // first user/password pair on the page). With these decoys, the real
          // SMTP/IMAP fields are left untouched.
          <form
            onSubmit={form.handleSubmit((data) => void salveaza(data))}
            className="space-y-6"
            noValidate
            autoComplete="off"
          >
            <input type="text" name="username" autoComplete="username" className="hidden" />
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              className="hidden"
            />

            <section className="space-y-3">
              <div className="border-b border-b4l-border pb-2">
                <h2 className="text-sm font-medium">Adresa de pe care trimite CRM-ul (SMTP)</h2>
                <p className="mt-1 text-xs text-b4l-text-muted">
                  De obicei no-reply@boost4life.ro. Mailurile automate primesc la final rândul
                  „Acesta este un mesaj automat… nu răspunde”; răspunsurile la cererile către
                  tipografii, design sau influenceri merg la căsuța citită, de mai jos.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field
                  label="Host"
                  className="col-span-2"
                  autoComplete="off"
                  {...form.register('smtpHost')}
                  error={form.formState.errors.smtpHost?.message}
                  disabled={!canWrite}
                />
                <Field
                  label="Port"
                  type="number"
                  autoComplete="off"
                  {...form.register('smtpPort')}
                  error={form.formState.errors.smtpPort?.message}
                  disabled={!canWrite}
                />
              </div>
              <Field
                label="Utilizator SMTP"
                autoComplete="off"
                spellCheck={false}
                placeholder="ex: no-reply@boost4life.ro"
                {...form.register('smtpUser')}
                error={form.formState.errors.smtpUser?.message}
                disabled={!canWrite}
              />
              <PasswordField
                label="Parola SMTP"
                autoComplete="new-password"
                hint={
                  settings?.smtpPassSet
                    ? 'Parola e setată. Lasă gol ca s-o păstrezi.'
                    : 'Nicio parolă setată încă.'
                }
                {...form.register('smtpPassword')}
                error={form.formState.errors.smtpPassword?.message}
                disabled={!canWrite}
              />
              {canWrite && (
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate('smtp')}
                  loading={testMutation.isPending && testMutation.variables === 'smtp'}
                  className="!text-xs"
                >
                  Testează conectarea SMTP
                </Button>
              )}
            </section>

            <section className="space-y-3">
              <div className="border-b border-b4l-border pb-2">
                <h2 className="text-sm font-medium">Căsuța principală citită de CRM (IMAP)</h2>
              </div>
              <div className="rounded border border-b4l-amber-light bg-b4l-amber-light p-3 text-xs text-b4l-amber-dark">
                Aici e <strong>contabilitate@boost4life.ro</strong>: din ea preia CRM-ul extrasele,
                facturile, mailurile de la eMAG și Rompetrol, răspunsurile la cereri și task-urile
                din mail. Nu o schimba când schimbi adresa de trimitere. Celelalte căsuțe (ex:
                contact@) se adaugă mai jos, la „Căsuțe suplimentare”.
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field
                  label="Host"
                  className="col-span-2"
                  autoComplete="off"
                  {...form.register('imapHost')}
                  error={form.formState.errors.imapHost?.message}
                  disabled={!canWrite}
                />
                <Field
                  label="Port"
                  type="number"
                  autoComplete="off"
                  {...form.register('imapPort')}
                  error={form.formState.errors.imapPort?.message}
                  disabled={!canWrite}
                />
              </div>
              <Field
                label="Utilizator IMAP"
                autoComplete="off"
                spellCheck={false}
                {...form.register('imapUser')}
                error={form.formState.errors.imapUser?.message}
                disabled={!canWrite}
              />
              <PasswordField
                label="Parola IMAP"
                autoComplete="new-password"
                hint={
                  settings?.imapPassSet
                    ? 'Parola e setată. Lasă gol ca s-o păstrezi.'
                    : 'Nicio parolă setată încă.'
                }
                {...form.register('imapPassword')}
                error={form.formState.errors.imapPassword?.message}
                disabled={!canWrite}
              />

              {canWrite && (
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate('imap')}
                  loading={testMutation.isPending && testMutation.variables === 'imap'}
                  className="!text-xs"
                >
                  Testează conectarea IMAP
                </Button>
              )}
            </section>

            {testResult && (
              <div
                className={`rounded border px-3 py-2 text-sm ${
                  testResult.ok
                    ? 'border-b4l-green-light bg-b4l-green-light text-b4l-green-dark'
                    : 'border-b4l-red-light bg-b4l-red-light text-b4l-red-dark'
                }`}
              >
                <strong>{testResult.service.toUpperCase()}: </strong>
                {testResult.ok ? 'conectare reușită' : `eșuat — ${testResult.error}`}
              </div>
            )}

            {canWrite && (
              <div className="flex justify-end pt-2">
                <Button type="submit" loading={saveMutation.isPending}>
                  Salvează
                </Button>
              </div>
            )}
          </form>
        )}
      </Card>

      <InboundMailboxesSection canWrite={canWrite} />
    </div>
  )
}

// ============================================================================
// Căsuțe suplimentare (IMAP) scanate pentru task-uri — ex: contact@boost4life.ro
// ============================================================================

interface Mailbox {
  id: number
  email: string
  label: string | null
  imapHost: string
  imapPort: number
  imapUser: string
  passSet: boolean
  /** Parola refuzată de server: căsuța stă pe loc până o salvezi din nou. */
  imapEroare: string | null
  active: boolean
  scanTasks: boolean
  shared: boolean
  defaultAssigneeId: number | null
  defaultAssignee: { id: number; name: string; email: string } | null
  /** „Task-urile merg la" (19.10.2026): task-urile devin private ale acestor oameni. */
  taskuriLaIds: number[]
  taskuriLa: { id: number; name: string; email: string; inactiv: boolean }[]
  imapLastSeenAt: string | null
}

interface MailboxForm {
  email: string
  label: string
  imapHost: string
  imapPort: number
  imapUser: string
  imapPassword: string
  active: boolean
  scanTasks: boolean
  shared: boolean
  defaultAssigneeId: string
  taskuriLaIds: number[]
}

function emptyForm(): MailboxForm {
  return {
    email: '',
    label: '',
    imapHost: '',
    imapPort: 993,
    imapUser: '',
    imapPassword: '',
    active: true,
    scanTasks: true,
    shared: false,
    defaultAssigneeId: '',
    taskuriLaIds: [],
  }
}

function InboundMailboxesSection({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Mailbox | 'new' | null>(null)

  const listQuery = useQuery<{ mailboxes: Mailbox[] }>({
    queryKey: ['inbound-mailboxes'],
    queryFn: async () =>
      (await api.get<{ mailboxes: Mailbox[] }>('/settings/inbound-mailboxes')).data,
  })
  const usersQuery = useQuery<{ users: { id: number; name: string; email: string }[] }>({
    queryKey: ['settings-users-min'],
    queryFn: async () =>
      (await api.get<{ users: { id: number; name: string; email: string }[] }>('/settings/users'))
        .data,
  })
  const mailboxes = listQuery.data?.mailboxes ?? []
  const users = usersQuery.data?.users ?? []

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/settings/inbound-mailboxes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbound-mailboxes'] })
      toast.success('Căsuță ștearsă')
    },
    onError: () => toast.error('Eroare la ștergere'),
  })

  return (
    <Card
      title="Căsuțe suplimentare (scanate pentru task-uri)"
      description="Pe lângă căsuța principală de mai sus, aceste căsuțe IMAP sunt scanate periodic și generează task-uri automat (ex: contact@boost4life.ro). Trimiterea de email (SMTP) nu e afectată."
    >
      {listQuery.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      ) : (
        <div className="space-y-3">
          {mailboxes.length === 0 ? (
            <p className="text-sm text-b4l-text-muted">Nicio căsuță suplimentară.</p>
          ) : (
            <ul className="divide-y divide-b4l-border">
              {mailboxes.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{m.email}</span>
                      {!m.active && (
                        <span className="rounded bg-b4l-bg px-1.5 py-0.5 text-xs text-b4l-text-muted">
                          inactiv
                        </span>
                      )}
                      {!m.passSet && (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                          fără parolă
                        </span>
                      )}
                      {m.imapEroare && (
                        <span
                          className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700"
                          title={m.imapEroare}
                        >
                          parola refuzată — oprită
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-b4l-text-muted">
                      {m.imapHost}:{m.imapPort} ·{' '}
                      {m.taskuriLa.length
                        ? `task-urile merg la ${m.taskuriLa.map((u) => (u.inactiv ? `${u.name} (inactiv)` : u.name)).join(', ')}, private`
                        : `autor: ${m.defaultAssignee?.name ?? 'implicit'}`}
                      {m.imapLastSeenAt
                        ? ` · ultim scan ${new Date(m.imapLastSeenAt).toLocaleString('ro-RO')}`
                        : ''}
                    </div>
                  </div>
                  {canWrite && (
                    <div className="flex gap-1">
                      <Button
                        variant="secondary"
                        onClick={() => setEditing(m)}
                        className="!px-2 !py-1 !text-sm"
                      >
                        Editează
                      </Button>
                      <button
                        onClick={() => {
                          if (confirm(`Ștergi căsuța ${m.email}?`)) del.mutate(m.id)
                        }}
                        className="rounded border border-red-200 px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                      >
                        Șterge
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canWrite && (
            <Button variant="secondary" onClick={() => setEditing('new')}>
              + Adaugă căsuță
            </Button>
          )}
        </div>
      )}

      {editing && (
        <MailboxModal
          mailbox={editing === 'new' ? null : editing}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            queryClient.invalidateQueries({ queryKey: ['inbound-mailboxes'] })
          }}
        />
      )}
    </Card>
  )
}

function MailboxModal({
  mailbox,
  users,
  onClose,
  onSaved,
}: {
  mailbox: Mailbox | null
  users: { id: number; name: string; email: string }[]
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!mailbox
  const [f, setF] = useState<MailboxForm>(() =>
    mailbox
      ? {
          email: mailbox.email,
          label: mailbox.label ?? '',
          imapHost: mailbox.imapHost,
          imapPort: mailbox.imapPort,
          imapUser: mailbox.imapUser,
          imapPassword: '',
          active: mailbox.active,
          scanTasks: mailbox.scanTasks,
          shared: mailbox.shared,
          defaultAssigneeId: mailbox.defaultAssigneeId ? String(mailbox.defaultAssigneeId) : '',
          taskuriLaIds: mailbox.taskuriLaIds,
        }
      : emptyForm(),
  )
  const set = <K extends keyof MailboxForm>(k: K, v: MailboxForm[K]) =>
    setF((p) => ({ ...p, [k]: v }))
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const body = () => ({
    email: f.email.trim(),
    label: f.label.trim() || null,
    imapHost: f.imapHost.trim(),
    imapPort: f.imapPort,
    imapUser: f.imapUser.trim(),
    ...(f.imapPassword ? { imapPassword: f.imapPassword } : {}),
    active: f.active,
    scanTasks: f.scanTasks,
    shared: f.shared,
    defaultAssigneeId: f.defaultAssigneeId ? Number(f.defaultAssigneeId) : null,
    taskuriLaIds: f.taskuriLaIds,
  })

  const save = useMutation({
    mutationFn: async () =>
      isEdit
        ? api.patch(`/settings/inbound-mailboxes/${mailbox!.id}`, body())
        : api.post('/settings/inbound-mailboxes', body()),
    onSuccess: () => {
      toast.success(isEdit ? 'Căsuță salvată' : 'Căsuță adăugată')
      onSaved()
    },
    onError: (err: unknown) =>
      toast.error(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la salvare',
      ),
  })

  const test = useMutation({
    mutationFn: async () => {
      const payload = {
        ...(mailbox ? { id: mailbox.id } : {}),
        host: f.imapHost.trim() || undefined,
        port: f.imapPort,
        user: f.imapUser.trim() || undefined,
        password: f.imapPassword || undefined,
      }
      return (
        await api.post<{ ok: boolean; error?: string }>('/settings/inbound-mailboxes/test', payload)
      ).data
    },
    onMutate: () => setTestMsg(null),
    onSuccess: (d) =>
      setTestMsg({ ok: d.ok, text: d.ok ? 'Conectare reușită' : `Eșuat — ${d.error}` }),
    onError: () => setTestMsg({ ok: false, text: 'Eroare la test' }),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Editează — ${mailbox!.email}` : 'Adaugă căsuță'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {isEdit ? 'Salvează' : 'Adaugă'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Email" value={f.email} onChange={(e) => set('email', e.target.value)} />
          <Field
            label="Etichetă (opțional)"
            value={f.label}
            onChange={(e) => set('label', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field
            label="Host IMAP"
            className="col-span-2"
            value={f.imapHost}
            onChange={(e) => set('imapHost', e.target.value)}
          />
          <Field
            label="Port"
            type="number"
            value={f.imapPort}
            onChange={(e) => set('imapPort', Number(e.target.value))}
          />
        </div>
        <Field
          label="Utilizator IMAP"
          value={f.imapUser}
          onChange={(e) => set('imapUser', e.target.value)}
        />
        <PasswordField
          label="Parolă IMAP"
          autoComplete="new-password"
          hint={mailbox?.passSet ? 'Parolă setată. Lasă gol ca s-o păstrezi.' : 'Setează parola.'}
          value={f.imapPassword}
          onChange={(e) => set('imapPassword', e.target.value)}
        />
        <div>
          {/*
            „Task-urile merg la" (Madalin, 19.10.2026): contact@ → Victor; si Adrian (28.10.2026).
            Inlocuieste departamentul „Admin". Nimeni = pe departamente, ca orice casuta.
          */}
          <label className="mb-1 block text-sm font-medium" htmlFor="taskuri-la-adauga">
            Task-urile merg la
          </label>
          {f.taskuriLaIds.length > 0 && (
            <ul className="mb-2 flex flex-wrap gap-1.5">
              {f.taskuriLaIds.map((id) => {
                const u = users.find((x) => x.id === id)
                const dinLista = mailbox?.taskuriLa.find((x) => x.id === id)
                const nume =
                  (u?.name ?? dinLista?.name ?? `#${id}`) + (dinLista?.inactiv ? ' (inactiv)' : '')
                return (
                  <li
                    key={id}
                    className="flex items-center gap-1 rounded-full border border-b4l-border bg-b4l-bg px-2.5 py-0.5 text-sm"
                  >
                    {nume}
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          'taskuriLaIds',
                          f.taskuriLaIds.filter((x) => x !== id),
                        )
                      }
                      className="ml-0.5 rounded px-1 text-b4l-text-muted hover:text-b4l-text"
                      aria-label={`Scoate-l pe ${nume}`}
                    >
                      ×
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          <select
            id="taskuri-la-adauga"
            value=""
            onChange={(e) => {
              const id = Number(e.target.value)
              if (id && !f.taskuriLaIds.includes(id)) set('taskuriLaIds', [...f.taskuriLaIds, id])
            }}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">
              {f.taskuriLaIds.length
                ? '+ Adaugă un coleg'
                : '— departamentele, după filtru (ca acum) —'}
            </option>
            {users
              .filter((u) => !f.taskuriLaIds.includes(u.id))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
          </select>
          <p className="mt-1 text-xs text-b4l-text-muted">
            Dacă alegi colegi, emailurile care cer o acțiune devin task-uri <b>private</b> ale lor,
            ale tuturor odată, cu departamentul propus de filtru. Le văd doar ei, oricare le poate
            trimite mai departe dintr-un clic și e „Gata” când îl închide oricare. Căsuța apare în
            Inbox doar la ei (și la cine urmărește mesajele private). Dacă schimbi colegii,
            task-urile existente rămân la cine le are.
          </p>
        </div>
        <div>
          {/* Din 12.10.2026 task-urile din mail intra la „De preluat", fara responsabil; omul ales
              aici apare doar ca autor („creat de"). */}
          <label className="mb-1 block text-sm font-medium">
            Autorul task-urilor din această căsuță
          </label>
          <select
            value={f.defaultAssigneeId}
            onChange={(e) => set('defaultAssigneeId', e.target.value)}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— implicit (ca acum) —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-b4l-text-muted">
            Apare la „creat de”. Task-urile intră la „De preluat” în departamentul de care țin și le
            ia cine are timp.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => set('active', e.target.checked)}
            />
            Activă
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.scanTasks}
              onChange={(e) => set('scanTasks', e.target.checked)}
            />
            Scanează pentru task-uri
          </label>
          {/*
            Fără asta, Inboxul e gol pentru toți cei care nu sunt admini: filtrul era „căsuța cu
            emailul contului tău", iar căsuțele citite sunt două de firmă, pe care nu are contul
            nimeni. Adela și Mădălin vedeau zero mesaje, deși au dreptul de Inbox.
          */}
          <label
            className="flex items-center gap-2 text-sm"
            title="Toți cei cu drept de Inbox văd mesajele din această căsuță. Fără bifă, o văd doar adminii."
          >
            <input
              type="checkbox"
              checked={f.shared}
              onChange={(e) => set('shared', e.target.checked)}
            />
            Vizibilă pentru toată echipa
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => test.mutate()}
            loading={test.isPending}
            className="!text-sm"
          >
            Test conexiune IMAP
          </Button>
          {testMsg && (
            <span className={`text-sm ${testMsg.ok ? 'text-b4l-green-dark' : 'text-b4l-red-dark'}`}>
              {testMsg.text}
            </span>
          )}
        </div>
      </div>
    </Modal>
  )
}
